import * as go from 'gojs';
import { PortSpot, SymbolDef, themeColor } from './gojs-schematic.symbols';
import { isPortLinked, withoutUndo } from './gojs-schematic.tools';

const CIM_ISSUE_COLOR = '#ef4444';
const CIM_INFO_COLOR = '#f59e0b';
export const CIM_FRAME = 'CIM_FRAME';
export const CIM_TAG = 'CIM_TAG';

function cimIssueMarkers(sym: SymbolDef, lengthAxis?: 'width' | 'height'): go.GraphObject[] {
  const $ = go.GraphObject.make;
  const { w, h, selection } = sym;
  const pad = 2 * Math.max(0, selection.padding) + 4;
  const isMarked = (d: Record<string, unknown>) => !!(d['cimIssue'] || d['cimInfo']);
  const color = (d: Record<string, unknown>) => d['cimIssue'] ? CIM_ISSUE_COLOR : CIM_INFO_COLOR;

  return [
    $(go.Shape, selection.shape, {
      name: CIM_FRAME,
      alignment: go.Spot.Center,
      width: Math.max(1, w + pad),
      height: Math.max(1, h + pad),
      fill: null,
      strokeWidth: 2,
      visible: false,
    },
      new go.Binding('visible', '', isMarked),
      new go.Binding('stroke', '', color),
      ...(lengthAxis
        ? [new go.Binding(lengthAxis, 'length', (len: number) => Math.max(1, len || (lengthAxis === 'width' ? w : h)) + pad)]
        : [])
    ),
    $(go.TextBlock, {
      name: CIM_TAG,
      alignment: go.Spot.Top,
      alignmentFocus: go.Spot.Bottom,
      margin: new go.Margin(0, 0, 4, 0),
      font: 'bold 10px Inter, sans-serif',
      visible: false,
    },
      new go.Binding('visible', '', isMarked),
      new go.Binding('stroke', '', color),
      new go.Binding('text', '', (d: Record<string, unknown>) => d['cimIssue'] ? 'WARNING' : 'INFO')
    ),
  ];
}

export function busbarNode(sym: SymbolDef, isLocked: () => boolean, isLight: () => boolean): go.Node {
  const $ = go.GraphObject.make;
  const { gen, w, h, defaultColor, selection } = sym;

  const toUri = (color: string, length: number) =>
    'data:image/svg+xml,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${length} ${h}" width="${length}" height="${h}">${gen(themeColor(color, isLight()), 'closed', length)}</svg>`
    );

  const rightClickTooltip = $(go.Adornment, 'Auto',
    $(go.Shape, 'RoundedRectangle', { fill: '#070e1a', stroke: '#2563eb', strokeWidth: 1 },
      new go.Binding('fill', '', () => isLight() ? '#ffffff' : '#070e1a')
    ),
    $(go.TextBlock,
      { margin: 6, stroke: '#ffffff', font: '11px Inter, sans-serif' },
      new go.Binding('stroke', '', () => themeColor('#ffffff', isLight())),
      'Right-click and drag from the busbar to connect equipment'
    )
  );

  return $(go.Node, 'Spot',
    {
      locationSpot: go.Spot.Center,
      movable: true,
      cursor: 'pointer',
      resizable: false,
      selectionAdorned: false,
      selectionChanged: (node: go.Part) => {
        const n = node as go.Node;
        const lbl = n.data?.labelKey ? n.diagram?.findNodeForKey(n.data.labelKey) : null;
        if (lbl) lbl.isSelected = node.isSelected;
      },
      mouseEnter: (_e: go.InputEvent, node: go.GraphObject) => {
        const n = node as go.Node;
        n.toolTip = isLocked() ? null : rightClickTooltip;
        if (!n.diagram) return;
        withoutUndo(n.diagram, () => {
          const lbl = n.data?.labelKey ? n.diagram!.findNodeForKey(n.data.labelKey) : null;
          if (lbl?.data?.hiddenUntilHover) lbl.opacity = 1;
        });
      },
      mouseLeave: (_e: go.InputEvent, node: go.GraphObject) => {
        const n = node as go.Node;
        if (!n.diagram) return;
        withoutUndo(n.diagram, () => {
          const lbl = n.data?.labelKey ? n.diagram!.findNodeForKey(n.data.labelKey) : null;
          if (lbl?.data?.hiddenUntilHover) lbl.opacity = 0;
        });
      },
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    new go.Binding('angle', 'angle'),
    new go.Binding('scale', 'nodeScale'),

    $(go.Picture, { name: 'BAR_PICTURE', source: toUri(defaultColor, w), width: w, height: h },
      new go.Binding('source', '', (d: Record<string, unknown>) =>
        toUri(
          (d['color'] as string) || defaultColor,
          Math.max(1, (d['length'] as number) || w)
        )
      ),
      new go.Binding('width', 'length', (len: number) => Math.max(1, len || w))
    ),

    $(go.Shape, selection.shape, {
      alignment: go.Spot.Center,
      width: Math.max(1, w + 2 * selection.padding),
      height: Math.max(1, h + 2 * selection.padding),
      fill: null,
      stroke: null,
      strokeWidth: selection.strokeWidth,
      strokeDashArray: selection.strokeDashArray,
    },
      new go.Binding('width', 'length',
        (len: number) => Math.max(1, (len || w)) + 2 * Math.max(0, selection.padding)
      ),
      new go.Binding('stroke', 'isSelected', (s: boolean) => s ? themeColor(selection.strokeColor, isLight()) : null).ofObject()
    ),

    ...cimIssueMarkers(sym, 'width'),

    $(go.Shape, 'Rectangle', {
      portId: '',
      fromLinkable: true,
      toLinkable: true,
      fill: 'transparent',
      stroke: null,
      stretch: go.Stretch.Fill,
      cursor: 'pointer',
    }),
  ) as go.Node;
}

export function acLineNode(sym: SymbolDef, isLight: () => boolean): go.Node {
  const $ = go.GraphObject.make;
  const { gen, w, h, defaultColor, ports, selection } = sym;
  const mkSpot = (p: PortSpot) => new go.Spot(p.x, p.y, p.ox ?? 0, p.oy ?? 0);

  const toUri = (color: string, length: number) =>
    'data:image/svg+xml,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${length}" width="${w}" height="${length}">${gen(themeColor(color, isLight()), 'closed', length)}</svg>`
    );

  const portDot = (portId: string, align: go.Spot) =>
    $(go.Shape, 'Circle', {
      portId,
      alignment: align,
      width: 8, height: 8,
      fill: '#ffffff',
      stroke: null,
      opacity: 1,
      cursor: 'crosshair',
      fromLinkable: true,
      toLinkable: true,
    },
      // Bound to 'category' because it never changes, so this only runs when the node is created or the
      // theme switches; a '' binding would also run on every data change and wipe linked ports' link colour.
      new go.Binding('fill', 'category', () => themeColor('#ffffff', isLight()))
    );

  const showPorts = (_e: go.InputEvent, node: go.GraphObject) => {
    const n = node as go.Node;
    if (!n.diagram) return;
    withoutUndo(n.diagram, () => {
      ['T', 'B'].forEach(pid => {
        const p = n.findPort(pid);
        if (!p || p === n) return;
        if (!isPortLinked(n, pid)) (p as go.Shape).fill = '#22c55e';
      });
    });
  };

  const hidePorts = (_e: go.InputEvent, node: go.GraphObject) => {
    const n = node as go.Node;
    if (!n.diagram) return;
    withoutUndo(n.diagram, () => {
      ['T', 'B'].forEach(pid => {
        const p = n.findPort(pid);
        if (!p || p === n) return;
        if (!isPortLinked(n, pid))
          (p as go.Shape).fill = n.isSelected ? '#22c55e' : themeColor('#ffffff', isLight());
      });
    });
  };

  return $(go.Node, 'Spot',
    {
      locationSpot: go.Spot.Center,
      movable: true,
      cursor: 'pointer',
      resizable: false,
      selectionAdorned: false,
      mouseEnter: showPorts,
      mouseLeave: hidePorts,
      selectionChanged: (node: go.Part) => {
        const n = node as go.Node;
        const lbl = n.data?.labelKey ? n.diagram?.findNodeForKey(n.data.labelKey) : null;
        if (lbl) lbl.isSelected = node.isSelected;
        if (!n.diagram) return;
        withoutUndo(n.diagram, () => {
          ['T', 'B'].forEach(pid => {
            const p = n.findPort(pid);
            if (!p || p === n) return;
            if (!isPortLinked(n, pid))
              (p as go.Shape).fill = node.isSelected ? '#22c55e' : themeColor('#ffffff', isLight());
          });
        });
      },
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    new go.Binding('angle', 'angle'),
    new go.Binding('scale', 'nodeScale'),

    // Picture is the main (first) element — its height drives the panel bounds
    // so the T/B port dot alignments (Spot 0% and 100%) track the line ends as length changes
    $(go.Picture, { source: toUri(defaultColor, h), width: w, height: h },
      new go.Binding('source', '', (d: Record<string, unknown>) =>
        toUri(
          (d['color'] as string) || defaultColor,
          Math.max(1, (d['length'] as number) || h)
        )
      ),
      new go.Binding('height', 'length', (len: number) => Math.max(1, len || h))
    ),

    $(go.Shape, selection.shape, {
      alignment: go.Spot.Center,
      width: Math.max(1, w + 2 * selection.padding),
      height: Math.max(1, h + 2 * selection.padding),
      fill: null,
      stroke: null,
      strokeWidth: selection.strokeWidth,
      strokeDashArray: selection.strokeDashArray,
    },
      new go.Binding('height', 'length',
        (len: number) => Math.max(1, (len || h)) + 2 * Math.max(0, selection.padding)
      ),
      new go.Binding('stroke', 'isSelected', (s: boolean) => s ? themeColor(selection.strokeColor, isLight()) : null).ofObject()
    ),

    $(go.Shape, 'Rectangle', { fill: 'transparent', stroke: null, stretch: go.Stretch.Fill }),

    ...cimIssueMarkers(sym, 'height'),

    portDot('T', mkSpot(ports.T)),
    portDot('B', mkSpot(ports.B)),
  ) as go.Node;
}

export function symNode(sym: SymbolDef, isLight: () => boolean): go.Node {
  const $ = go.GraphObject.make;
  const { gen, w, h, defaultColor, ports, selection } = sym;
  const mkSpot = (p: PortSpot) => new go.Spot(p.x, p.y, p.ox ?? 0, p.oy ?? 0);

  const toUri = (color: string, state = 'closed', length = w) =>
    'data:image/svg+xml,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">${gen(themeColor(color, isLight()), state, length, isLight() ? '#ffffff' : undefined)}</svg>`
    );

  const portDot = (portId: string, align: go.Spot) =>
    $(go.Shape, 'Circle', {
      portId,
      alignment: align,
      width: 8, height: 8,
      fill: '#22c55e',
      stroke: null,
      opacity: 0,
      cursor: 'crosshair',
      fromLinkable: true,
      toLinkable: true,
    });

  const showPorts = (_e: go.InputEvent, node: go.GraphObject) => {
    const n = node as go.Node;
    if (!n.diagram) return;
    withoutUndo(n.diagram, () => {
      ['T', 'R', 'B', 'L'].forEach(pid => {
        const p = n.findPort(pid);
        if (p) p.opacity = 1;
      });
      const lbl = n.data?.labelKey ? n.diagram!.findNodeForKey(n.data.labelKey) : null;
      if (lbl?.data?.hiddenUntilHover) lbl.opacity = 1;
    });
  };

  const hidePorts = (_e: go.InputEvent, node: go.GraphObject) => {
    const n = node as go.Node;
    if (!n.diagram) return;
    withoutUndo(n.diagram, () => {
      ['T', 'R', 'B', 'L'].forEach(pid => {
        const p = n.findPort(pid);
        if (!p || p === n) return;
        if (!isPortLinked(n, pid)) p.opacity = n.isSelected ? 1 : 0;
      });
      const lbl = n.data?.labelKey ? n.diagram!.findNodeForKey(n.data.labelKey) : null;
      if (lbl?.data?.hiddenUntilHover) lbl.opacity = 0;
    });
  };

  return $(go.Node, 'Spot',
    {
      locationSpot: go.Spot.Center,
      movable: true,
      cursor: 'pointer',
      resizable: false,
      selectionAdorned: false,
      mouseEnter: showPorts,
      mouseLeave: hidePorts,
      selectionChanged: (node: go.Part) => {
        const n = node as go.Node;
        const lbl = n.data?.labelKey ? n.diagram?.findNodeForKey(n.data.labelKey) : null;
        if (lbl) lbl.isSelected = node.isSelected;
        if (!n.diagram) return;
        withoutUndo(n.diagram, () => {
          ['T', 'R', 'B', 'L'].forEach(pid => {
            const p = n.findPort(pid);
            if (!p || p === n) return;
            if (!isPortLinked(n, pid)) p.opacity = node.isSelected ? 1 : 0;
          });
        });
      },
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    new go.Binding('angle', 'angle'),
    new go.Binding('scale', 'nodeScale'),

    $(go.Shape, 'Rectangle', {
      fill: 'transparent', stroke: null, stretch: go.Stretch.Fill,
    }),

    $(go.Picture, { source: toUri(defaultColor), width: w, height: h },
      new go.Binding('source', '', (d: Record<string, unknown>) =>
        toUri((d['color'] as string) || defaultColor, (d['state'] as string) || 'closed')
      ),
    ),

    $(go.Shape, selection.shape, {
      alignment: go.Spot.Center,
      width: Math.max(1, w + 2 * selection.padding),
      height: Math.max(1, h + 2 * selection.padding),
      fill: null,
      stroke: null,
      strokeWidth: selection.strokeWidth,
      strokeDashArray: selection.strokeDashArray,
    },
      new go.Binding('stroke', 'isSelected', (s: boolean) => s ? themeColor(selection.strokeColor, isLight()) : null).ofObject()
    ),

    ...cimIssueMarkers(sym),

    portDot('T', mkSpot(ports.T)),
    portDot('R', mkSpot(ports.R)),
    portDot('B', mkSpot(ports.B)),
    portDot('L', mkSpot(ports.L)),
  ) as go.Node;
}
